import { useEffect, useRef, useState } from 'preact/hooks';
import { Sheet } from '../components/ui';
import { Cover } from '../components/Cover';
import type { Work } from '../../shared/types';
import { searchCandidates, storeFromUrl, type Candidate } from '../lib/covers';
import { blobToBitmap, uploadCover, type Crop } from '../lib/images';
import { updateWork } from '../lib/actions';
import { toast } from '../components/toast';

// „Schimbă coperta”: sugestii automate, poză proprie (cu încadrare), link, sau copertă 3D.
export function CoverPicker({ work, onClose }: { work: Work; onClose: () => void }) {
  const [cands, setCands] = useState<Candidate[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [photo, setPhoto] = useState<ImageBitmap | null>(null);
  const [link, setLink] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async (fresh = false) => {
    setCands(null);
    try { setCands(await searchCandidates(work, fresh)); } catch (e) { setCands([]); toast((e as Error).message, 'error'); }
  };
  useEffect(() => { void load(); }, [work.id]);

  const choose = async (url: string, source: 'verified' | 'manual' = 'verified') => {
    setBusy(url);
    try { await storeFromUrl(work.id, url, source); toast('Copertă salvată 📚'); onClose(); }
    catch (e) { toast(`Nu am putut folosi imaginea: ${(e as Error).message}`, 'error'); }
    setBusy(null);
  };

  const onFile = async (f?: File | null) => {
    if (!f) return;
    try { setPhoto(await blobToBitmap(f)); } catch { toast('Nu pot citi imaginea', 'error'); }
  };

  if (photo) return <CropSheet bmp={photo} onCancel={() => setPhoto(null)} onDone={async crop => {
    setBusy('photo');
    try {
      const url = await uploadCover(photo, crop, { workId: work.id, source: 'photo' });
      await updateWork(work.id, { cover_url: url, cover_source: 'photo' });
      toast('Fotografia copertei a fost salvată 📸'); onClose();
    } catch (e) { toast((e as Error).message, 'error'); }
    setBusy(null);
  }} busy={busy === 'photo'} />;

  return (
    <Sheet open onClose={onClose} title="Schimbă coperta">
      <div class="flex gap-3 items-start">
        <div class="w-20 shrink-0"><Cover work={work} /></div>
        <div class="text-sm text-ink-700">
          <div class="font-semibold">{work.title}</div>
          <div class="text-ink-500">{work.authors}</div>
          <p class="mt-1 text-xs text-ink-500">Atinge coperta potrivită. Prima variantă este cea pe care aplicația o alege automat.</p>
        </div>
      </div>

      <div class="mt-4 flex items-center justify-between">
        <span class="label !mb-0">Sugestii (okazii, librării RO, Google Books, Open Library)</span>
        <button class="text-xs font-semibold text-terra-600" onClick={() => load(true)}>Caută din nou</button>
      </div>
      {cands === null ? <div class="py-8 text-center text-sm text-ink-500">Se caută coperți…</div> :
        cands.length === 0 ? <div class="py-6 text-center text-sm text-ink-500">N-am găsit nimic online. Fă o poză coperții 👇</div> : (
          <div class="mt-2 grid grid-cols-3 gap-3" data-testid="cover-candidates">
            {cands.map((c, i) => (
              <button key={c.url} class="text-left disabled:opacity-50" disabled={!!busy} onClick={() => choose(c.url)}>
                <div class="relative aspect-[2/3] rounded-lg overflow-hidden bg-cream-200 shadow-book">
                  <img src={c.thumb || c.url} referrerpolicy="no-referrer" loading="lazy" class="h-full w-full object-cover"
                    onError={e => { (e.target as HTMLImageElement).style.opacity = '0.15'; }} />
                  {i === 0 && <span class="absolute left-1 top-1 rounded bg-terra-500 px-1.5 text-[10px] font-bold text-white">auto</span>}
                  {busy === c.url && <span class="absolute inset-0 flex items-center justify-center bg-white/70 text-xs font-semibold">se salvează…</span>}
                </div>
                <div class="mt-1 text-[10px] text-ink-500 truncate">{c.ro ? '🇷🇴 ' : ''}{c.source}</div>
              </button>
            ))}
          </div>
        )}

      <div class="mt-5 grid grid-cols-2 gap-2">
        <button class="btn-primary" onClick={() => fileRef.current?.click()}>📸 Fac o poză</button>
        <button class="btn-ghost" onClick={async () => { await updateWork(work.id, { cover_url: null, cover_source: null }); toast('Copertă 3D'); onClose(); }}>🎨 Copertă 3D</button>
      </div>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" class="hidden" onChange={e => onFile((e.target as HTMLInputElement).files?.[0])} />
      <div class="mt-4 rounded-xl border border-cream-300 bg-white p-3 space-y-2">
        <div class="text-xs text-ink-500">Nu e printre sugestii? Caut-o tu pe Google: ține degetul apăsat pe imagine → <b>Copiază adresa imaginii</b> → revino aici → <b>Lipește</b>.</div>
        <div class="flex gap-2">
          <a class="btn-ghost flex-1 text-xs" target="_blank" rel="noopener"
            href={`https://www.google.com/search?tbm=isch&hl=ro&q=${encodeURIComponent(`${work.title} ${(work.authors || '').split(';')[0]} carte coperta`)}`}>🔎 Google Imagini ↗</a>
          <button class="btn-ghost flex-1 text-xs" onClick={async () => {
            try { const t = (await navigator.clipboard.readText()).trim(); if (/^https?:\/\//.test(t)) { setLink(t); void choose(t, 'manual'); } else toast('În clipboard nu e un link de imagine', 'error'); }
            catch { toast('Lipește linkul manual în câmpul de mai jos', 'error'); }
          }}>📋 Lipește</button>
        </div>
        <div class="flex gap-2">
          <input class="field text-sm" placeholder="sau lipește aici linkul imaginii…" value={link} onInput={e => setLink((e.target as HTMLInputElement).value)} />
          <button class="btn-ghost" disabled={!link.trim() || !!busy} onClick={() => choose(link.trim(), 'manual')}>Folosește</button>
        </div>
      </div>
    </Sheet>
  );
}

// Încadrare 2:3: tragi chenarul cu degetul, mărimea din glisor
export function CropSheet({ bmp, onCancel, onDone, busy }: { bmp: ImageBitmap; onCancel: () => void; onDone: (c: Crop) => void; busy?: boolean }) {
  const W = bmp.width, H = bmp.height;
  const maxFw = Math.min(W, (H * 2) / 3);
  const [s, setS] = useState(0.95);
  const [c, setC] = useState({ x: 0.5, y: 0.5 });
  const boxRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fw = maxFw * s, fh = fw * 1.5;
  const cx = Math.min(Math.max(c.x * W, fw / 2), W - fw / 2);
  const cy = Math.min(Math.max(c.y * H, fh / 2), H - fh / 2);
  const crop: Crop = { x: Math.round(cx - fw / 2), y: Math.round(cy - fh / 2), w: Math.round(fw), h: Math.round(fh) };

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const scale = Math.min(1, 900 / Math.max(W, H));
    cv.width = Math.round(W * scale); cv.height = Math.round(H * scale);
    cv.getContext('2d')!.drawImage(bmp, 0, 0, cv.width, cv.height);
  }, [bmp]);

  const drag = useRef<{ px: number; py: number; x: number; y: number } | null>(null);
  const onDown = (e: PointerEvent) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); drag.current = { px: e.clientX, py: e.clientY, x: cx / W, y: cy / H }; };
  const onMove = (e: PointerEvent) => {
    if (!drag.current || !boxRef.current) return;
    const r = boxRef.current.getBoundingClientRect();
    setC({ x: drag.current.x + (e.clientX - drag.current.px) / r.width, y: drag.current.y + (e.clientY - drag.current.py) / r.height });
  };

  return (
    <Sheet open onClose={onCancel} title="Încadrează coperta"
      footer={<><button class="btn-ghost" onClick={() => onDone({ x: 0, y: 0, w: W, h: H })} disabled={busy}>Toată poza</button><button class="btn-primary" onClick={() => onDone(crop)} disabled={busy}>{busy ? 'Se salvează…' : 'Salvează'}</button></>}>
      <div ref={boxRef} class="relative mx-auto select-none touch-none" style={{ width: `min(100%, calc(60dvh * ${W / H}))`, aspectRatio: `${W}/${H}` }}>
        <canvas ref={canvasRef} class="h-full w-full rounded-lg" />
        <div class="absolute border-2 border-white rounded-md cursor-move"
          style={{ left: `${(crop.x / W) * 100}%`, top: `${(crop.y / H) * 100}%`, width: `${(crop.w / W) * 100}%`, height: `${(crop.h / H) * 100}%`, boxShadow: '0 0 0 9999px rgba(0,0,0,.45)' }}
          onPointerDown={onDown as any} onPointerMove={onMove as any} onPointerUp={() => (drag.current = null)} />
      </div>
      <label class="mt-4 block text-xs text-ink-500">Mărime chenar
        <input type="range" min="0.3" max="1" step="0.01" value={s} onInput={e => setS(Number((e.target as HTMLInputElement).value))} class="w-full accent-terra-500" />
      </label>
      <p class="text-xs text-ink-500">Trage chenarul peste copertă. Aplicația păstrează doar ce e în interior.</p>
    </Sheet>
  );
}
